import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Carousel } from "antd";
import { Pie } from "@ant-design/charts";
import { Link } from "react-router-dom";
import GetPerformanceStatsApi from "../../api/performance/GetPerformanceStatsApi";
import { SimpleLoader } from "../sub/Loaders";
import Theme from "../../theme/Theme";

function CombinedPerformanceCarousel() {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const carouselRef = React.useRef();
  
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    getPerformanceStats();
  }, [perfCycleState]);

  async function getPerformanceStats() {
    setLoading(true);
    const apiResponse = await GetPerformanceStatsApi(
      positionState?.id,
      perfCycleState?.id
    );

    if (apiResponse.success) {
      setPerformanceData(apiResponse?.data);
      setLoading(false);
    }
  }

  const getPieConfig = (data) => ({
    appendPadding: 10,
    data: data || [],
    angleField: "value",
    colorField: "type",
    radius: 1,
    innerRadius: 0.6,
    legend: false,
    label: false,
    theme: {
      colors10: ["#f50", "#87d068", "#FAB324"],
    },
    interactions: [
      {
        type: "element-selected",
      },
      {
        type: "element-active",
      },
    ],
    statistic: {
      title: false,
      content: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        content: "",
      },
    },
  });

  const PerformanceCard = ({ title, percentage, chartData, linkTo }) => {
    const totalPlans = chartData?.length === 3
      ? chartData[0]?.value + chartData[1]?.value + chartData[2]?.value
      : 0;

    return (
      <Link
        to={linkTo}
        className="block text-black hover:text-black"
      >
        <div className="mx-2">
          {/* Four sections side by side with borders */}
          <div className="grid grid-cols-4 gap-0">
            {/* Performance Percentage - label changes based on type */}
            <div 
              className="p-4 text-center"
              style={{ 
                borderRight: `1px solid ${Theme().navBorder}`,
              }}
            >
              <div 
                className="text-xs mb-2" 
                style={{ 
                  color: Theme().textPrimary,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 300,
                }}
              >
                {title}:
              </div>
              <div 
                className="text-2xl font-semibold" 
                style={{ color: Theme().textPrimary }}
              >
                {parseFloat(percentage || 0).toFixed(2)}%
              </div>
            </div>

            {/* Plans On Track */}
            <div 
              className="p-4 text-center"
              style={{ 
                borderRight: `1px solid ${Theme().navBorder}`,
              }}
            >
              <div 
                className="text-xs mb-2" 
                style={{ 
                  color: Theme().textPrimary,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 300,
                }}
              >
                Plans On Track:
              </div>
              <div 
                className="text-2xl font-semibold" 
                style={{ color: Theme().primary }}
              >
                {chartData?.[1]?.value || 0}
              </div>
            </div>

            {/* Plans Off Track */}
            <div 
              className="p-4 text-center"
              style={{ 
                borderRight: `1px solid ${Theme().navBorder}`,
              }}
            >
              <div 
                className="text-xs mb-2" 
                style={{ 
                  color: Theme().textPrimary,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 300,
                }}
              >
                Plans Off Track:
              </div>
              <div 
                className="text-2xl font-semibold" 
                style={{ color: '#FAB324' }}
              >
                {chartData?.[2]?.value || 0}
              </div>
            </div>

            {/* Attention Required / At Risk */}
            <div className="p-4 text-center">
              <div 
                className="text-xs mb-2" 
                style={{ 
                  color: Theme().textPrimary,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 300,
                }}
              >
                Attention Required:
              </div>
              <div 
                className="text-2xl font-semibold" 
                style={{ color: Theme().attentionCritical }}
              >
                {chartData?.[0]?.value || 0}
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="mb-10">
        <SimpleLoader />
      </div>
    );
  }

  return (
    <div className="mb-10" style={{ position: 'relative' }}>
      <style>{carouselStyles}</style>
      <Carousel
        ref={carouselRef}
        autoplay
        autoplaySpeed={7000}
        dots={true}
        dotPosition="bottom"
        style={{ paddingBottom: '40px' }}
      >
        {/* Organization Performance */}
        <div>
          <PerformanceCard
            title="Organization performance"
            percentage={performanceData?.organization?.performance}
            chartData={performanceData?.organization?.data}
            linkTo="/top-plans"
          />
        </div>

        {/* Unit Performance */}
        <div>
          <PerformanceCard
            title="Unit performance"
            percentage={performanceData?.unit?.performance}
            chartData={performanceData?.unit?.data}
            linkTo={`/units/${performanceData?.unit?.unitId}`}
          />
        </div>

        {/* Your Performance */}
        <div>
          <PerformanceCard
            title="Your performance"
            percentage={performanceData?.individual?.performance}
            chartData={performanceData?.individual?.data}
            linkTo={`/positions-performance/${performanceData?.individual?.positionId}`}
          />
        </div>
      </Carousel>
    </div>
  );
}

export default CombinedPerformanceCarousel;


// Custom CSS for carousel dots
const carouselStyles = `
  .ant-carousel .slick-dots li button {
    background: rgba(241, 90, 36, 0.38) !important;
    width: 8px !important;
    height: 8px !important;
    border-radius: 50% !important;
  }
  
  .ant-carousel .slick-dots li.slick-active button {
    background: #F15A24 !important;
    width: 10px !important;
    height: 10px !important;
  }
`;
